import { useState, useEffect, useCallback } from "react";
import { Menu } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCalendar,
  faCalendarCheck,
  faChartPie,
  faChalkboard,
  faCubes,
  faDice,
  faEnvelope,
  faFileArchive,
  faFileInvoiceDollar,
  faFire,
  faHandHoldingUsd,
  faPager,
  faPaperclip,
  faPercentage,
  faPhone,
  faTachometerAlt,
  faUserFriends,
  faUserShield,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import { useGetCurrentUserQuery } from "../../features/identity/identityApi";

export const SiderMenu = () => {
  const { data: currentUser, isLoading, isUninitialized, isFetching } = useGetCurrentUserQuery();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const onSelect = useCallback((args: any) => {
    setSelectedKeys(args.selectedKeys);
  }, []);

  const { pathname } = useLocation();

  useEffect(() => {
    let menuKey = pathname
      .split("/")
      .filter((s) => s)
      .shift();
    if (!menuKey) {
      menuKey = "dashboard";
    }

    setSelectedKeys([menuKey]);
  }, [pathname]);

  const checkPolicy = useCallback(
    (policy: string) => {
      if (!currentUser?.claims) return false;

      const [claimKey, claimValue] = policy.split("-");
      return currentUser.claims[claimKey]?.includes(claimValue);
    },
    [currentUser?.claims],
  );

  const hasAccess = useCallback(
    (policies: Array<string>, some: boolean = false) => {
      if (policies.includes("MonthlyEnergy-Read")) {
      }
      if (!policies?.length) return true;
      if (!currentUser?.claims) return false;

      return some ? policies.some(checkPolicy) : policies.every(checkPolicy);
    },
    [checkPolicy, currentUser?.claims],
  );

  if (isLoading || isUninitialized || isFetching) {
    return null;
  }

  return (
    <Menu
      theme="light"
      className="bg-slate-200 scrollbar-thin"
      style={{
        height: "calc(100vh - 100px)",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "thin",
      }}
      mode="inline"
      selectedKeys={selectedKeys}
      onSelect={onSelect}>
      <Menu.Item key="dashboard" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faTachometerAlt} />}>
        <Link to="/">داشبورد</Link>
      </Menu.Item>
      {hasAccess(["Industry-Read", "Company-Read", "CompanyIntro-Read", "CompanyIdentity-Read"], true) && (
        <Menu.SubMenu key="base" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faCubes} />} title="اطلاعات پایه">
          {hasAccess(["Industry-Read"]) && (
            <Menu.Item key="industries" icon>
              <Link to="/industries">صنایع</Link>
            </Menu.Item>
          )}

          {hasAccess(["ActivityGroup-Read"]) && (
            <Menu.Item key="activityGroups" icon>
              <Link to="/activityGroups">گروه‌های فعالیت</Link>
            </Menu.Item>
          )}

          {hasAccess(["Company-Read"]) && (
            <Menu.Item key="companies" icon>
              <Link to="/companies">شرکت ها</Link>
            </Menu.Item>
          )}

          {hasAccess(["CompanyIdentity-Read"]) && (
            <Menu.Item key="companyIdentities" icon>
              <Link to="/companyIdentities">شناسه شرکت ها</Link>
            </Menu.Item>
          )}

          {hasAccess(["Product-Read"]) && (
            <Menu.Item key="products" icon>
              <Link to="/products">محصولات</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["Announcement-Read"], true) && (
        <Menu.SubMenu
          key="announcementsMenu"
          icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faFileArchive} />}
          title="مدیریت اطلاعیه‌ها">
          <Menu.Item key="announcements" icon>
            <Link to="/announcements">اطلاعیه‌ها</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["InvestmentMonthlyPerformance-Scrap", "InvestmentMonthlyPerformance-Update"], true) && (
        <Menu.SubMenu key="investmentMenu" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faChartPie} />} title="سرمایه‌گذاری">
          {hasAccess(["InvestmentMonthlyPerformance-Update"]) && (
            <Menu.Item key="investmentReports" icon>
              <Link to="/investmentReport">گزارش‌های سرمایه‌گذاری</Link>
            </Menu.Item>
          )}
          {hasAccess(["InvestmentMonthlyPerformance-Scrap", "InvestmentMonthlyPerformance-Update"]) && (
            <Menu.Item key="investmentReportEditor" icon>
              <Link to="/investmentReport/new">اسکرپ / ویرایش دستی</Link>
            </Menu.Item>
          )}
          {hasAccess(["InvestmentMonthlyPerformance-Update"]) && (
            <Menu.Item key="investmentReview" icon>
              <Link to="/investmentReview">کنترل کیفیت سرمایه‌گذاری</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["FinancialStatement-Read"], true) && (
        <Menu.SubMenu
          key="financialStatementsMenu"
          icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faFileArchive} />}
          title="صورت‌های مالی">
          <Menu.Item key="profitAndLoss" icon>
            <Link to="/profitAndLoss">سود و زیان</Link>
          </Menu.Item>
          <Menu.Item key="balanceSheet" icon>
            <Link to="/balanceSheet">ترازنامه</Link>
          </Menu.Item>
          <Menu.Item key="cashFlow" icon>
            <Link to="/cashFlow">صورت نقدی</Link>
          </Menu.Item>
          <Menu.Item key="dps" icon>
            <Link to="/dps">سود نقدی</Link>
          </Menu.Item>
          <Menu.SubMenu key="consolidatedFinancialStatementsMenu" icon title="تلفیقی">
            <Menu.Item key="consolidatedProfitAndLoss" icon>
              <Link to="/consolidatedProfitAndLoss">سود و زیان</Link>
            </Menu.Item>
            <Menu.Item key="consolidatedBalanceSheet" icon>
              <Link to="/consolidatedBalanceSheet">ترازنامه</Link>
            </Menu.Item>
            <Menu.Item key="consolidatedCashFlow" icon>
              <Link to="/consolidatedCashFlow">صورت نقدی</Link>
            </Menu.Item>
          </Menu.SubMenu>
          <Menu.Item key="oie" icon>
            <Link to="/oie">درآمدهای عملیاتی</Link>
          </Menu.Item>
          <Menu.Item key="cost" icon>
            <Link to="/cost">بهای تمام شده</Link>
          </Menu.Item>
          <Menu.Item key="oacost" icon>
            <Link to="/oacost">سربار و اداری</Link>
          </Menu.Item>
          <Menu.Item key="inventoryTurnover" icon>
            <Link to="/inventoryTurnover">موجودی کالا</Link>
          </Menu.Item>
          <Menu.Item key="inventoryTurnoverEstimated" icon>
            <Link to="/inventoryTurnoverEstimated">برآورد موجودی کالا</Link>
          </Menu.Item>
          <Menu.Item key="pcRawMaterial" icon>
            <Link to="/pcRawMaterial">خ.م. مواد اولیه</Link>
          </Menu.Item>
          <Menu.Item key="pcRawMaterialEstimated" icon>
            <Link to="/pcRawMaterialEstimated">برآورد خ.م. مواد اولیه</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["MonthlyPerformance-Read"], true) && (
        <Menu.SubMenu
          key="monthlyPerformancesMenu"
          icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faCalendarCheck} />}
          title="عملکرد ماهانه">
          <Menu.Item key="monthlyPerformance" icon>
            <Link to="/monthlyPerformance">عملکرد ماهانه</Link>
          </Menu.Item>
          <Menu.Item key="cumulativeMonthlyPerformance" icon>
            <Link to="/cumulativeMonthlyPerformance">عملکرد ماهانه تجمعی</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["MonthlyRawMaterial-Read"], true) && (
        <Menu.SubMenu
          key="monthlyRawMaterialsMenu"
          icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faCalendarCheck} />}
          title="مواد اولیه ماهانه">
          <Menu.Item key="monthlyRawMaterial" icon>
            <Link to="/monthlyRawMaterial">مواد اولیه ماهانه</Link>
          </Menu.Item>
          <Menu.Item key="cumulativeMonthlyRawMaterial" icon>
            <Link to="/cumulativeMonthlyRawMaterial">مواد اولیه ماهانه تجمعی</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["FcFlow-Read"], true) && (
        <Menu.SubMenu key="fcFlowsMenu" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faCalendarCheck} />} title="منابع و مصارف ارزی">
          <Menu.Item key="fcFlow" icon>
            <Link to="/fcFlow">منابع و مصارف ارزی</Link>
          </Menu.Item>
          <Menu.Item key="cumulativeFcFlow" icon>
            <Link to="/cumulativeFcFlow">منابع و مصارف ارزی تجمعی</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["MonthlyEnergy-Read"], true) && (
        <Menu.SubMenu key="monthlyEnergiesMenu" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faFire} />} title="انرژی ماهانه">
          <Menu.Item key="monthlyEnergy" icon>
            <Link to="/monthlyEnergy">انرژی ماهانه</Link>
          </Menu.Item>
          <Menu.Item key="cumulativeMonthlyEnergy" icon>
            <Link to="/cumulativeMonthlyEnergy">انرژی ماهانه تجمعی</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}
      {hasAccess(["CompanyIntro-Read", "ArticleCategory-Read", "Article-Read"], true) && (
        <Menu.SubMenu key="content" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faPager} />} title="مدیریت محتوا">
          {hasAccess(["ArticleCategory-Read"]) && (
            <Menu.Item key="articleCategories" icon>
              <Link to="/articleCategories">گروه‌های مقالات</Link>
            </Menu.Item>
          )}

          {hasAccess(["ArticleSubCategory-Read"]) && (
            <Menu.Item key="articleSubCategories" icon>
              <Link to="/articleSubCategories">زیرگروه‌های مقالات</Link>
            </Menu.Item>
          )}

          {hasAccess(["AuthorGroup-Read"]) && (
            <Menu.Item key="authorgroups" icon>
              <Link to="/authorGroups">گروه‌های نویسندگان</Link>
            </Menu.Item>
          )}

          {hasAccess(["Author-Read"]) && (
            <Menu.Item key="authors" icon>
              <Link to="/authors">نویسندگان</Link>
            </Menu.Item>
          )}

          {hasAccess(["AuthorGroup-Read", "Author-Read"]) && (
            <Menu.Item key="agRequests" icon>
              <Link to="/agRequests" title="درخواست افزودن نویسنده به گروه">
                درخواست افزودن نویسنده
              </Link>
            </Menu.Item>
          )}

          {hasAccess(["BankAccount-Read"]) && (
            <Menu.Item key="bankAccounts" icon>
              <Link to="/bankAccounts" title="حساب‌های بانکی">
                حساب‌های بانکی
              </Link>
            </Menu.Item>
          )}

          {hasAccess(["AuthorCooperationRequest-Read"]) && (
            <Menu.Item key="authorCooperations" icon>
              <Link to="/authorCooperations" title="درخواست‌های همکاری نویسندگان">
                درخواست‌های همکاری نویسندگان
              </Link>
            </Menu.Item>
          )}

          {hasAccess(["ArticleContract-Read"]) && (
            <Menu.Item key="articleContracts" icon>
              <Link to="/articleContracts" title="قراردادهای مقالات">
                قراردادهای مقالات
              </Link>
            </Menu.Item>
          )}

          {hasAccess(["Article-Read"]) && (
            <Menu.Item key="articles" icon>
              <Link to="/articles">مقالات</Link>
            </Menu.Item>
          )}

          {hasAccess(["ArticleDurationType-Read"]) && (
            <Menu.Item key="articleDurationTypes" icon>
              <Link to="/articleDurationTypes">انواع دوره مقالات</Link>
            </Menu.Item>
          )}

          {hasAccess(["ArticleSubscription-Read"]) && (
            <Menu.Item key="articleSubscriptions" icon>
              <Link to="/articleSubscriptions">اشتراک‌های مقالات</Link>
            </Menu.Item>
          )}

          {hasAccess(["ArticleInvoice-Read"]) && (
            <Menu.Item key="articleInvoices" icon>
              <Link to="/articleInvoices">صورت‌حساب‌های مقالات</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["ShareholderGroup-Read", "Shareholder-Read", "CompanyShareholder-Read"], true) && (
        <Menu.SubMenu key="shareholdersMenu" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faUserFriends} />} title="سهامدارن">
          {hasAccess(["ShareholderGroup-Read"]) && (
            <Menu.Item key="shareholderGroups" icon>
              <Link to="/shareholderGroups">گروه های سهامداران</Link>
            </Menu.Item>
          )}

          {hasAccess(["Shareholder-Read"]) && (
            <Menu.Item key="shareholders" icon>
              <Link to="/shareholders">سهامداران</Link>
            </Menu.Item>
          )}

          {hasAccess(["CompanyShareholder-Read"]) && (
            <Menu.Item key="companyShareholders" icon>
              <Link to="/companyShareholders">سهامداران شرکت ها</Link>
            </Menu.Item>
          )}

          {hasAccess(["CompanyShareholder-Read"]) && (
            <Menu.Item key="shareholdersEnterLeave" icon>
              <Link to="/shareholdersEnterLeave">ورود/خروج سهامداران</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["GeneralInfo-Read", "Glance-Read"], true) && (
        <Menu.SubMenu
          key="boardAndMarketInfo"
          icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faChalkboard} />}
          title="اطلاعات بازار و تابلو">
          {hasAccess(["GeneralInfo-Read"]) && (
            <Menu.Item key="generalInfos" icon>
              <Link to="/generalInfos/bourse">اطلاعات کلی بازار</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glances" icon>
              <Link to="/glances">نگاه‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glancesCache" icon>
              <Link to="/glancesCache">کش نگاه‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glanceComputations" icon>
              <Link to="/glanceComputations">محاسبات نگاه‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="computedPe" icon>
              <Link to="/computedPe">محاسبه P/E</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glancesCollect" icon>
              <Link to="/glancesCollect">جمع‌آوری نگاه‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glanceAdjustments" icon>
              <Link to="/glanceAdjustments">نگاه‌های تعدیل</Link>
            </Menu.Item>
          )}
          {hasAccess(["Glance-Read"]) && (
            <Menu.Item key="glanceAdjustmentMarks" icon>
              <Link to="/glanceAdjustmentMarks">مارک‌های تعدیل</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["StatInfo-Read"], true) && (
        <Menu.SubMenu key="statistics" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faDice} />} title="آمار">
          {hasAccess(["StatInfo-Read"]) && (
            <Menu.Item key="statInfos" icon>
              <Link to="/statInfos">اطلاعات آماری</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["Subscription-Read", "Discount-Read", "Package-Read"], true) && (
        <Menu.SubMenu title="حسابداری" key="subs-management" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faHandHoldingUsd} />}>
          {hasAccess(["Package-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faUserShield} />} key="package-management">
              <Link to="/accounts">حساب‌های کاربری</Link>
            </Menu.Item>
          )}
          {hasAccess(["Subscription-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faFileInvoiceDollar} />} key="subs-list">
              <Link to="/subscriptions">اشتراک‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Package-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faPaperclip} />} key="packages">
              <Link to="/packages">بسته‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Package-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faCalendar} />} key="durations">
              <Link to="/durations">دوره‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Discount-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faPercentage} />} key="discounts">
              <Link to="/discounts">تخفیف‌ها</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["ContactMessage-Read", "Message-Read"], true) && (
        <Menu.SubMenu title="مدیریت تماس‌ها" key="contactMsg-management" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faPhone} />}>
          {hasAccess(["Message-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faEnvelope} />} key="messages">
              <Link to="/messages">پیام‌ها</Link>
            </Menu.Item>
          )}
          {hasAccess(["Notification-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faBell} />} key="notifications">
              <Link to="/notifications">اعلانات</Link>
            </Menu.Item>
          )}
          {hasAccess(["ContactMessage-Read"]) && (
            <Menu.Item icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faPhone} />} key="contactMessage">
              <Link to="/contactMessage">تماس با ما</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {hasAccess(["User-Read"], true) && (
        <Menu.SubMenu key="userManagement" icon={<FontAwesomeIcon className="ant-menu-item-icon" icon={faUsers} />} title="کاربران">
          {hasAccess(["User-Read"]) && (
            <Menu.Item key="users" icon>
              <Link to="/users">کاربران</Link>
            </Menu.Item>
          )}
          {hasAccess(["ScoringConfig-Read"]) && (
            <Menu.Item key="scoringConfigs" icon>
              <Link to="/scoringConfigs">تنظیمات امتیازدهی</Link>
            </Menu.Item>
          )}
          {hasAccess(["ScoringLog-Read"]) && (
            <Menu.Item key="scoringlogs" icon>
              <Link to="/scoringLogs">سوابق امتیازدهی</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
    </Menu>
  );
};
